import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Refresh, Search as SearchIcon } from '@mui/icons-material'
import { DEFAULT_SCHEDULER_TIMELINE_HOURS, SCHEDULER_TIMELINE_HOURS, type ScheduledRunOutcome, type ScheduledRunTimeline } from '@numeo/types'
import { Spinner } from '../_ds/Primitives'
import { schedulersApi } from './api'
import { formatDuration, formatRelative, formatTime, OUTCOME_LABEL } from './format'
import { formatRate, gridStepMs, gridTicks, OUTCOME_FILL, OUTCOME_SWATCH, toLanes } from './timeline'

/** The timeline re-reads this often while it is open, like the job list. */
const REFRESH_EVERY_MS = 30_000
/** The lane is drawn in these units and stretched to its width. */
const LANE_UNITS = 1000
/** A run shorter than a pixel or so still needs a visible mark. */
const MIN_MARK_UNITS = 1.5
const OUTCOMES: ScheduledRunOutcome[] = ['succeeded', 'failed', 'timed_out', 'skipped']

const clockLabel = (t: number): string => new Date(t).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })

/**
 * Every run of every job over the last hours, one lane per job, busiest job first: a dense lane is a job that fires
 * often, a gap is a job that stopped. Each mark is one run, as wide as it took and coloured by how it ended.
 */
export function RunsTimeline() {
    const [hours, setHours] = useState<number>(DEFAULT_SCHEDULER_TIMELINE_HOURS)
    const [timeline, setTimeline] = useState<ScheduledRunTimeline | null>(null)
    const [error, setError] = useState('')
    const [loading, setLoading] = useState(true)
    const [search, setSearch] = useState('')
    const [now, setNow] = useState(Date.now())

    // Polls never stack, and only the newest request may set the view: switching windows asks again at once, and the
    // answer for the window just left is dropped.
    const inFlight = useRef(false)
    const latest = useRef(0)

    const load = useCallback(async (asked: number, force = false) => {
        if (inFlight.current && !force) return
        inFlight.current = true
        const id = ++latest.current
        setLoading(true)
        try {
            const data = await schedulersApi.timeline(asked)
            if (id === latest.current) {
                setTimeline(data)
                setError('')
            }
        } catch {
            if (id === latest.current) setError('Could not load the run history')
        } finally {
            if (id === latest.current) {
                inFlight.current = false
                setNow(Date.now())
                setLoading(false)
            }
        }
    }, [])

    // Reads the window at once when it changes, then polls it only while the tab is visible.
    useEffect(() => {
        load(hours, true)
        const id = setInterval(() => {
            if (!document.hidden) load(hours)
        }, REFRESH_EVERY_MS)
        const onVisible = () => {
            if (!document.hidden) load(hours)
        }
        document.addEventListener('visibilitychange', onVisible)
        return () => {
            clearInterval(id)
            document.removeEventListener('visibilitychange', onVisible)
        }
    }, [hours, load])

    const lanes = useMemo(() => toLanes(timeline?.runs ?? [], search), [timeline, search])
    const busiest = lanes[0]?.runs.length ?? 0
    const ticks = useMemo(() => (timeline ? gridTicks(timeline.from, timeline.to, gridStepMs(timeline.hours)) : []), [timeline])
    const span = timeline ? timeline.to - timeline.from : 1
    const at = (t: number): number => ((t - (timeline?.from ?? 0)) / span) * LANE_UNITS

    return (
        <div>
            <div className="mb-4 flex flex-wrap items-center gap-2">
                {SCHEDULER_TIMELINE_HOURS.map((value) => (
                    <button
                        key={value}
                        onClick={() => setHours(value)}
                        className={[
                            'rounded-full px-3 py-1 text-xs font-medium ring-1 ring-inset transition-colors',
                            hours === value ? 'bg-indigo-600 text-white ring-indigo-600' : 'bg-white text-gray-600 ring-gray-200 hover:bg-gray-50',
                        ].join(' ')}
                    >
                        Last {value} h
                    </button>
                ))}
                <div className="ml-3 flex flex-wrap items-center gap-3 text-xs text-gray-500">
                    {OUTCOMES.map((outcome) => (
                        <span key={outcome} className="flex items-center gap-1.5">
                            <span className={`h-2.5 w-2.5 rounded-sm ${OUTCOME_SWATCH[outcome]}`} />
                            {OUTCOME_LABEL[outcome]}
                        </span>
                    ))}
                </div>
                <div className="relative ml-auto flex items-center gap-2">
                    <SearchIcon sx={{ fontSize: 16 }} className="pointer-events-none absolute left-2.5 top-2 text-gray-400" />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search jobs"
                        className="h-8 w-56 rounded-lg border border-gray-200 bg-white pl-8 pr-3 text-sm outline-none focus:border-indigo-300"
                    />
                    <button
                        onClick={() => load(hours, true)}
                        className="flex h-8 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-600 transition-colors hover:border-gray-300 hover:bg-gray-50"
                    >
                        {loading ? <Spinner size={14} /> : <Refresh sx={{ fontSize: 16 }} />}
                        Refresh
                    </button>
                </div>
            </div>

            {error && <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>}
            {timeline?.truncated && (
                <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                    More runs than one view can hold: the oldest are left out. Pick a shorter window to see all of it.
                </div>
            )}

            <div className="overflow-x-auto rounded-xl border border-gray-100 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
                <div className="min-w-[720px]">
                    {/* Time axis */}
                    <div className="flex border-b border-gray-100 text-[11px] text-gray-400 dark:border-slate-700">
                        <div className="w-96 shrink-0 px-3 py-2 font-semibold uppercase tracking-wider">Job · runs</div>
                        <div className="relative mr-3 h-8 flex-1">
                            {ticks.map((t) => (
                                <span key={t} className="absolute top-2 -translate-x-1/2 whitespace-nowrap" style={{ left: `${(at(t) / LANE_UNITS) * 100}%` }}>
                                    {clockLabel(t)}
                                </span>
                            ))}
                        </div>
                    </div>

                    {lanes.map((lane) => (
                        <div
                            key={lane.job}
                            className="flex items-center border-b border-gray-50 last:border-b-0 hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-800/40"
                        >
                            <div className="flex w-96 shrink-0 items-center gap-2 px-3 py-1">
                                <span className="min-w-0 flex-1 truncate font-mono text-xs text-gray-700 dark:text-gray-300" title={lane.job}>
                                    {lane.job}
                                </span>
                                {/* How busy this job is next to the busiest one: the number to compare at a glance. */}
                                <span className="h-1.5 w-12 overflow-hidden rounded-full bg-gray-100 dark:bg-slate-800">
                                    <span className="block h-full rounded-full bg-indigo-400" style={{ width: `${(lane.runs.length / busiest) * 100}%` }} />
                                </span>
                                <span className="w-20 text-right text-[11px] tabular-nums text-gray-500">
                                    {lane.runs.length} · {formatRate(lane.runs.length, timeline?.hours ?? hours)}
                                </span>
                                {lane.failures > 0 && <span className="text-[11px] font-medium tabular-nums text-rose-600">{lane.failures}✕</span>}
                            </div>
                            <svg
                                className="mr-3 h-5 flex-1"
                                viewBox={`0 0 ${LANE_UNITS} 20`}
                                preserveAspectRatio="none"
                                role="img"
                                aria-label={`${lane.job} runs`}
                            >
                                {ticks.map((t) => (
                                    <line
                                        key={t}
                                        x1={at(t)}
                                        x2={at(t)}
                                        y1={0}
                                        y2={20}
                                        className="stroke-gray-100 dark:stroke-slate-800"
                                        vectorEffect="non-scaling-stroke"
                                    />
                                ))}
                                {lane.runs.map((run, i) => (
                                    <rect
                                        key={`${run.startedAt}-${i}`}
                                        x={at(run.startedAt)}
                                        y={3}
                                        width={Math.max((run.durationMs / span) * LANE_UNITS, MIN_MARK_UNITS)}
                                        height={14}
                                        className={OUTCOME_FILL[run.outcome]}
                                    >
                                        <title>
                                            {`${OUTCOME_LABEL[run.outcome]} · ${formatTime(new Date(run.startedAt).toISOString())} · ${formatDuration(run.durationMs)} · ${run.trigger}`}
                                        </title>
                                    </rect>
                                ))}
                            </svg>
                        </div>
                    ))}

                    {timeline && lanes.length === 0 && (
                        <div className="px-3 py-8 text-center text-sm text-gray-400">
                            {search ? 'No jobs match.' : `No runs in the last ${timeline.hours} h.`}
                        </div>
                    )}
                    {!timeline && loading && (
                        <div className="flex justify-center py-10">
                            <Spinner size={20} />
                        </div>
                    )}
                </div>
            </div>
            {timeline && (
                <p className="mt-2 text-xs text-gray-400">
                    {timeline.runs.length} runs from {lanes.length} jobs. Jobs that did not run are not shown. Updated{' '}
                    {formatRelative(new Date(timeline.to).toISOString(), now)}. Refreshes every 30 s.
                </p>
            )}
        </div>
    )
}
